/**
 * Per-tenant web branding (login page, header brand spot, collapsed sidebar,
 * tab title + favicon).
 *
 * The logo files are byte-identical copies of the phone app's assets so web
 * matches the phone:
 *   mobile/apps/aura_sales/assets/brand/sunstellar_logo_mark_green.png → light surfaces
 *   mobile/apps/aura_sales/assets/brand/sunstellar_logo_mark.png       → dark surfaces
 *   mobile/apps/aura_sales/android/app/src/sunstellar/res/mipmap-xxxhdpi/ic_launcher.png → small mark
 *
 * A tenant with no entry here keeps the AURA brand unchanged.
 *
 * Which tenant: the signed-in tenant's slug wins. Before sign-in (login page)
 * the hostname decides, then the optional build env `VITE_BRAND_TENANT`.
 */

export interface TenantBrand {
  slug: string;
  name: string;
  /** Wordmark for light backgrounds. */
  logoOnLight: string;
  /** Wordmark for dark backgrounds. Same pixel size as `logoOnLight`. */
  logoOnDark: string;
  /** width / height of the wordmark files — lets callers reserve the box. */
  logoAspect: number;
  /** Square mark (collapsed sidebar, favicon). */
  mark: string;
  /** Browser tab title. */
  title: string;
}

const BRANDS: Record<string, TenantBrand> = {
  sunstellar: {
    slug: 'sunstellar',
    name: 'Sun Stellar',
    logoOnLight: '/brand/sunstellar/sunstellar_logo_mark_green.png',
    logoOnDark: '/brand/sunstellar/sunstellar_logo_mark.png',
    logoAspect: 1257 / 424,
    mark: '/brand/sunstellar/ic_launcher.png',
    title: 'Sun Stellar · AURA',
  },
};

/** Customer-branded hosts → tenant slug (mirrors backend tenant middleware). */
const HOST_TO_SLUG: Record<string, string> = {
  'aura.sunstellar.in': 'sunstellar',
  'sunstellar-dev.aura.ardhanex.com': 'sunstellar',
};

export function resolveBrandSlug(
  tenantSlug: string | null | undefined,
  hostname: string | null | undefined,
): string | null {
  if (tenantSlug) return tenantSlug;
  const host = (hostname ?? '').trim().toLowerCase();
  if (host && HOST_TO_SLUG[host]) return HOST_TO_SLUG[host];
  const envSlug = (import.meta.env.VITE_BRAND_TENANT as string | undefined)?.trim();
  return envSlug || null;
}

export function tenantBrand(slug: string | null | undefined): TenantBrand | null {
  if (!slug) return null;
  return BRANDS[slug] ?? null;
}

export function tenantBrandFor(
  tenantSlug: string | null | undefined,
  hostname: string | null | undefined = typeof window !== 'undefined' ? window.location.hostname : null,
): TenantBrand | null {
  return tenantBrand(resolveBrandSlug(tenantSlug, hostname));
}

/** Point the tab title + favicon at the tenant brand. No-op without a brand. */
export function applyBrandToDocument(brand: TenantBrand | null): void {
  if (!brand || typeof document === 'undefined') return;
  if (document.title !== brand.title) document.title = brand.title;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== brand.mark) {
    link.type = 'image/png';
    link.href = brand.mark;
  }
}
